import { formatPeso, type Due, type LocalDate, type PayeeLike } from '@tahi/core';
import { dayOfMonthLabel, dayShort, monthLabel } from '../budget/format';

export {
  dayShort,
  daysAwayText,
  minorToInput,
  monthLabel,
  ordinal,
  parsePesoInput,
  spokenDate,
} from '../budget/format';

export const peso = (minor: number) => formatPeso(minor, { cents: 'auto' });

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** 'Feb 2027' from 'YYYY-MM'. */
export function monthShort(month: string): string {
  const full = monthLabel(month);
  return `${full.slice(0, 3)} ${month.slice(0, 4)}`;
}

/** 'Late · 3 days', 'Due today', 'Tomorrow', 'In 5 days'. */
export function whenText(daysAway: number): string {
  if (daysAway < 0) return `Late · ${-daysAway} ${-daysAway === 1 ? 'day' : 'days'}`;
  if (daysAway === 0) return 'Due today';
  if (daysAway === 1) return 'Tomorrow';
  return `In ${daysAway} days`;
}

/** 'Monthly on the 5th', 'Twice a month, 3rd and 18th', 'Every 2 months on the 5th', 'Weekly on Monday'. */
export function scheduleText(
  p: Pick<PayeeLike, 'repeat' | 'everyN' | 'dueDays' | 'startDate' | 'kind' | 'statementDay'> &
    Partial<Pick<PayeeLike, 'dueDaysAfterStatement'>>,
): string {
  if (p.kind === 'card' && p.dueDaysAfterStatement != null)
    return `Statement on the ${dayOfMonthLabel(p.statementDay ?? 1)}, due ${p.dueDaysAfterStatement} ${p.dueDaysAfterStatement === 1 ? 'day' : 'days'} after`;
  if (p.kind === 'card')
    return `Statement on the ${dayOfMonthLabel(p.statementDay ?? 1)}, due on the ${dayOfMonthLabel(p.dueDays[0] ?? 1)}`;
  const days = p.dueDays.map(dayOfMonthLabel);
  switch (p.repeat) {
    case 'none':
      return 'Pay any time';
    case 'weekly': {
      const day = WEEKDAYS[new Date(`${p.startDate}T00:00:00Z`).getUTCDay()];
      return p.everyN > 1 ? `Every ${p.everyN} weeks on ${day}` : `Weekly on ${day}`;
    }
    case 'yearly':
      return `Every year on ${dayShort(p.startDate).slice(5, 8)} ${days[0] ?? ''}`.trim();
    case 'monthly': {
      const on = days.length === 2 ? `the ${days[0]} and ${days[1]}` : `the ${days[0]}`;
      if (days.length === 2 && p.everyN === 1) return `Twice a month, ${on}`;
      return p.everyN > 1 ? `Every ${p.everyN} months on ${on}` : `Monthly on ${on}`;
    }
  }
}

/** 'Paid ₱1,000 of ₱1,499 · ₱499 left' (OPEN-QUESTIONS v0.5 Q2). */
export function partialText(d: Pick<Due, 'paidMinor' | 'amountMinor' | 'remainingMinor'>): string {
  return `Paid ${peso(d.paidMinor)} of ${peso(d.amountMinor)} · ${peso(d.remainingMinor)} left`;
}

/** 'Nov 5' */
export function dayMonth(d: LocalDate): string {
  return dayShort(d).slice(5);
}

export function interestText(bp: number | null): string {
  if (!bp) return 'No interest';
  return `${(bp / 100).toFixed(bp % 100 === 0 ? 0 : 2)}% a month`;
}

export const kindLabel: Record<PayeeLike['kind'], string> = {
  bill: 'Bill',
  utility: 'Utility',
  card: 'Credit card',
  loan: 'Loan',
};
